import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
    ClipboardDocumentCheckIcon,
    XCircleIcon,
    EyeIcon,
    BuildingOfficeIcon,
    CpuChipIcon,
    ShieldCheckIcon,
    TicketIcon,
    PaperClipIcon,
    XMarkIcon
} from '@heroicons/react/24/outline';
import i18n from '../i18n';

const API_URL = '/api/v1';

export default function AdminApprovals() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedItem, setSelectedItem] = useState(null);
    const [previewDoc, setPreviewDoc] = useState(null);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [accountsRes, devicesRes, certRes] = await Promise.all([
                axios.get(`${API_URL}/accounts/status/pending`),
                axios.get(`${API_URL}/devices/status/pending`),
                axios.get(`${API_URL}/certificates/status/pending`)
            ]);

            const formattedAccounts = accountsRes.data.map(a => ({
                id: a.id,
                type: 'COMPANY',
                name: a.account_name,
                owner: a.legal_representative || 'Unknown',
                date: new Date(a.created_at).toLocaleDateString(),
                raw: a
            }));

            const formattedDevices = devicesRes.data.map(d => ({
                id: d.id,
                type: 'DEVICE',
                name: d.device_name,
                owner: d.owner_name || 'System Entity',
                date: new Date(d.created_at).toLocaleDateString(),
                raw: d
            }));

            const formattedCerts = certRes.data.map(c => ({
                id: c.certificate_id,
                type: 'CERTIFICATE',
                name: `Issuance: ${c.output_mwh} MWh`,
                owner: c.gs1_id || 'Generating...',
                date: new Date(c.issue_date).toLocaleDateString(),
                raw: c
            }));

            setItems([...formattedAccounts, ...formattedDevices, ...formattedCerts]);
        } catch (error) {
            console.error('Error fetching approval queue:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleApprove = async (id, type) => {
        try {
            if (type === 'COMPANY') {
                await axios.put(`${API_URL}/accounts/${id}/approve`);
            } else if (type === 'DEVICE') {
                await axios.put(`${API_URL}/devices/${id}/approve`);
            } else if (type === 'CERTIFICATE') {
                await axios.put(`${API_URL}/certificates/${id}/mint`, {}, {
                    headers: { 'X-User-Role': 'ISSUING_BODY' }
                });
            }
            setItems(items.filter(item => item.id !== id));
            setSelectedItem(null);
        } catch (error) {
            alert('Action failed: Regulatory rejection.');
        }
    };

    if (loading) return (
        <div className="flex items-center justify-center py-40">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#155e75]" />
        </div>
    );

    return (
        <div className="space-y-6 animate-fade-in relative min-h-[600px] px-2 sm:px-4 md:px-0">

            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-b border-slate-100 pb-4">
                <div className="text-center md:text-left">
                    <h1 className="text-2xl font-bold text-slate-900 tracking-tight uppercase">{i18n.t('approval_queue')}</h1>
                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">{i18n.t('issuing_body_oversight')}</p>
                </div>

                <div className="flex bg-slate-100 rounded-xl p-1 border border-slate-200 self-center">
                    <button className="px-4 sm:px-6 py-2 rounded-lg bg-white text-[#155e75] text-[10px] font-bold uppercase tracking-wider shadow-sm">
                        Pending ({items.length})
                    </button>
                    <button className="px-4 sm:px-6 py-2 rounded-lg text-slate-400 text-[10px] font-bold uppercase tracking-wider hover:text-[#155e75] transition-colors">
                        Approved
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 items-start">

                <div className="lg:col-span-2 bg-white border border-[#adc6ce]/30 rounded-2xl overflow-hidden shadow-sm">
                    <div className="overflow-x-auto">
                        <table className="ent-table w-full text-left text-xs">
                            <thead>
                                <tr className="bg-slate-50/50 text-slate-400 font-bold uppercase tracking-wider border-b border-slate-100">
                                    <th className="py-4 px-5">{i18n.t('action_col')}</th>
                                    <th className="py-4">Entity / Request</th>
                                    <th className="py-4">Submission Date</th>
                                    <th className="py-4 text-right px-5">{i18n.t('actions_col')}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {items.map(item => (
                                    <tr
                                        key={item.id}
                                        className={`cursor-pointer transition-colors ${selectedItem?.id === item.id ? 'bg-blue-50/30 border-l-4 border-l-[#155e75]' : 'hover:bg-slate-50/20'}`}
                                        onClick={() => setSelectedItem(item)}
                                    >
                                        <td className="py-4 px-5">
                                            <span className={`inline-flex px-2.5 py-0.5 rounded-full text-[8px] font-bold uppercase border ${item.type === 'DEVICE' ? 'bg-indigo-50 border-indigo-200 text-indigo-700' : item.type === 'CERTIFICATE' ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-amber-50 border-amber-200 text-amber-700'}`}>
                                                {item.type}
                                            </span>
                                        </td>
                                        <td className="font-bold text-slate-800 text-[11px] uppercase tracking-tight py-4 max-w-[200px] truncate">{item.name}</td>
                                        <td className="text-slate-400 text-[10px] font-bold py-4">{item.date}</td>

                                        <td className="py-4 px-5 text-right">
                                            <div className="flex justify-end items-center w-full">
                                                <EyeIcon className="w-5 h-5 text-slate-400 hover:text-[#155e75] transition-colors" />
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {items.length === 0 && (
                        <div className="p-16 text-center flex flex-col items-center justify-center text-slate-400 uppercase tracking-widest font-bold text-xs">
                            <ClipboardDocumentCheckIcon className="w-12 h-12 text-slate-200 mb-4" />
                            {i18n.t('queue_empty') || 'No pending approvals'}
                        </div>
                    )}
                </div>

                <div className="lg:col-span-1">
                    {selectedItem ? (
                        <div className="bg-white border border-[#adc6ce]/30 rounded-2xl shadow-sm p-6 sm:p-8 sticky top-6 animate-fade-in">
                            <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-100">
                                <h3 className="font-bold text-slate-900 uppercase tracking-tight text-base sm:text-lg">
                                    {i18n.t('review_details')}
                                </h3>
                                <button onClick={() => setSelectedItem(null)} className="p-1 rounded-lg bg-slate-50 text-slate-400 hover:text-slate-600">
                                    <XMarkIcon className="w-5 h-5" />
                                </button>
                            </div>

                            <div className="space-y-6">
                                {selectedItem.type === 'DEVICE' ? (
                                    <div className="space-y-5">
                                        <div className="flex items-center gap-3 bg-[#155e75]/5 p-4 border-l-4 border-l-[#155e75] rounded-r-xl">
                                            <CpuChipIcon className="w-6 h-6 text-[#155e75]" />
                                            <div>
                                                <p className="text-[9px] font-bold text-[#155e75] uppercase tracking-wider">Asset Category</p>
                                                <p className="text-xs font-bold text-slate-800">{selectedItem.raw.h2_ready ? 'Green Hydrogen (Offre Maroc)' : 'Renewable Power'}</p>
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-1 gap-4">
                                            <DetailRow label="GSRN Number" value={selectedItem.raw.gsrn} />
                                            <DetailRow label="Grid Operator" value={selectedItem.raw.grid_operator_name} highlight />
                                            <DetailRow label="Capacity (MW)" value={`${selectedItem.raw.capacity_mw} MW`} />
                                        </div>
                                    </div>
                                ) : selectedItem.type === 'CERTIFICATE' ? (
                                    <div className="space-y-5">
                                        <div className="flex items-center gap-3 bg-emerald-50 p-4 border-l-4 border-l-emerald-600 rounded-r-xl">
                                            <TicketIcon className="w-6 h-6 text-emerald-600" />
                                            <div>
                                                <p className="text-[9px] font-bold text-emerald-600 uppercase tracking-wider">{i18n.t('is_request')}</p>
                                                <p className="text-xs font-bold text-slate-800">{selectedItem.raw.output_mwh} MWh Requested</p>
                                            </div>
                                        </div>
                                        <DetailRow label="Usage Category" value={i18n.t(`usage_${selectedItem.raw.usage_category.toLowerCase()}`)} highlight />
                                        <div className="grid grid-cols-2 gap-4">
                                            <DetailRow label="Period Start" value={new Date(selectedItem?.raw?.production_start || selectedItem.raw.production_start).toLocaleDateString()} />
                                            <DetailRow label="Period End" value={new Date(selectedItem?.raw?.production_end || selectedItem.raw.production_end).toLocaleDateString()} />
                                        </div>
                                        <div className="bg-slate-50 p-3 rounded-xl border border-slate-100 italic text-[9px] text-slate-500 font-bold leading-relaxed">
                                            Verification Seal: {selectedItem.raw.verification_seal_text}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="space-y-5">
                                        <div className="flex items-center gap-3 bg-amber-50 p-4 border-l-4 border-l-amber-500 rounded-r-xl">
                                            <ShieldCheckIcon className="w-6 h-6 text-amber-600" />
                                            <div>
                                                <p className="text-[9px] font-bold text-amber-600 uppercase tracking-wider">{i18n.t('kyc_review')}</p>
                                                <p className="text-xs font-bold text-slate-800">{i18n.t('comp_onboarding')}</p>
                                            </div>
                                        </div>
                                        <DetailRow label="Role Type" value={selectedItem.raw.account_type} highlight />
                                        <DetailRow label="VAT / License" value={selectedItem.raw.vat_number} />
                                        <DetailRow label="Legal Rep" value={selectedItem.raw.legal_representative} />
                                        <DetailRow label="Jurisdiction" value={selectedItem.raw.country_code} highlight />

                                        {selectedItem.raw.account_type === 'VVB_AUDITOR' && (
                                            <div className="pt-2 space-y-3">
                                                <p className="text-[9px] font-bold text-[#155e75] uppercase tracking-wider mb-2">Accreditation Documents</p>
                                                <DocumentLink label="Tax Certificate" filename={selectedItem.raw.doc_tax_certificate} onPreview={() => setPreviewDoc({ label: 'Tax Certificate', url: selectedItem.raw.doc_tax_certificate })} />
                                                <DocumentLink label="Reg. Certificate (RC)" filename={selectedItem.raw.doc_rc} onPreview={() => setPreviewDoc({ label: 'Reg. Certificate (RC)', url: selectedItem.raw.doc_rc })} />
                                                <DocumentLink label="Company Statutes" filename={selectedItem.raw.doc_statutes} onPreview={() => setPreviewDoc({ label: 'Company Statutes', url: selectedItem.raw.doc_statutes })} />
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div className="pt-6 border-t border-slate-100 flex flex-col xs:flex-row gap-3">
                                    <button
                                        onClick={() => handleApprove(selectedItem.id, selectedItem.type)}
                                        className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white py-3.5 rounded-xl font-bold text-xs uppercase tracking-wider shadow-lg shadow-emerald-600/10 text-center"
                                    >
                                        {selectedItem.type === 'CERTIFICATE' ? i18n.t('mint_go') : i18n.t('qualify_entity')}
                                    </button>
                                    <button className="flex-1 bg-red-600 hover:bg-red-700 text-white py-3.5 rounded-xl font-bold text-xs uppercase tracking-wider shadow-lg shadow-red-600/10 text-center">
                                        {i18n.t('reject')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="border border-dashed border-[#adc6ce]/40 rounded-2xl h-48 lg:h-full flex items-center justify-center p-6 text-center bg-white shadow-sm">
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest leading-relaxed">
                                Select a request to review manifest
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {previewDoc && (
                <div className="fixed inset-0 bg-slate-900/90 backdrop-blur-md z-[300] flex items-center justify-center p-4 sm:p-8 animate-fade-in">
                    <div className="bg-white w-full max-w-5xl h-[90vh] rounded-2xl flex flex-col shadow-2xl overflow-hidden">

                        <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-slate-50 flex-shrink-0">
                            <div className="min-w-0 flex-1">
                                <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider truncate">{previewDoc.label}</h4>
                                <p className="text-[9px] text-slate-400 font-semibold font-mono truncate max-w-full">{previewDoc.url}</p>
                            </div>
                            <button onClick={() => setPreviewDoc(null)} className="p-1.5 hover:bg-slate-200 rounded-lg transition-colors ml-4">
                                <XCircleIcon className="w-6 h-6 text-slate-400 hover:text-slate-600" />
                            </button>
                        </div>

                        <div className="flex-1 overflow-hidden bg-slate-100">
                            <iframe
                                src={previewDoc.url}
                                title={previewDoc.label}
                                className="w-full h-full border-0"
                            />
                        </div>

                        <div className="p-4 border-t border-slate-100 bg-white flex justify-end flex-shrink-0">
                            <a
                                href={previewDoc.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                download
                                className="w-full sm:w-auto bg-[#155e75] hover:bg-[#0e4e61] text-white px-6 py-3 rounded-xl text-xs font-bold uppercase tracking-wider shadow-lg text-center"
                            >
                                Download Original File
                            </a>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

const DocumentLink = ({ label, filename, onPreview }) => (
    <div className="flex items-center justify-between p-2.5 bg-indigo-50/50 border border-indigo-100 rounded-xl text-[10px] font-semibold">
        <span className="text-slate-500 uppercase">{label}</span>
        {filename ? (
            <button
                onClick={onPreview}
                className="text-[#155e75] flex items-center gap-1 hover:underline"
            >
                <PaperClipIcon className="w-3.5 h-3.5" /> {filename}
            </button>
        ) : (
            <span className="text-red-400 italic">NOT UPLOADED</span>
        )}
    </div>
);

const DetailRow = ({ label, value, highlight }) => (
    <div className="flex flex-col border-b border-slate-50 pb-2">
        <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-1 block">{label}</span>
        <span className={`text-xs font-bold ${highlight ? 'text-[#155e75]' : 'text-slate-700'}`}>{value || 'NOT PROVIDED'}</span>
    </div>
);